import React, { useEffect, useRef, useState } from 'react';
import useHomeLocation from '@/hooks/useHomeLocations';
import Sider from 'antd/es/layout/Sider';
import {
  ConfigProvider, Drawer, Grid, Layout, Menu, MenuProps,
} from 'antd';
import { Content, Footer, Header } from 'antd/es/layout/layout';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  InboxOutlined,
  ProfileOutlined,
  SettingOutlined,
  ShopOutlined,
  TagsOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import EBasicActions from '@/enums/EBasicActions';
import EPage from '@/enums/EPages';
import usePermissions from '@/hooks/usePermissions';
import { Outlet, useLocation } from 'react-router-dom';
import Topbar from '@/containers/Layout/topbar/Topbar';
import useCustomTranslation from '@/hooks/useCustomTranslation';
import { NavigationGuardProvider } from '@/context/NavigationGuardContext';
import { ActiveNavProvider } from '@/context/ActiveNavContext';
import { useActiveNavKeyValue } from '@/hooks/useActiveNavKey';
import useNavigationGuard from '@/hooks/useNavigationGuard';
import MenuSearch from './MenuSearch';

type TMenuItem = NonNullable<MenuProps['items']>[number];
// `section` is the text header the leaf is listed under; `aliases` are extra paths that also select it.
type TMenuLeaf = { key: string; label: string; section?: string; aliases?: string[] };
type TMenuGroup = { key: string; label: string; icon: React.ReactNode; children: TMenuLeaf[] };

const SIDER_WIDTH = 288;
const SIDER_COLLAPSED_WIDTH = 80;

// Measures follow the navigation mockup: 16px icons, 12px icon gap and 13px side padding.
const menuTheme = {
  components: {
    Menu: {
      iconSize: 16,
      collapsedIconSize: 16,
      iconMarginInlineEnd: 12,
      itemPaddingInline: 13,
      itemMarginInline: 8,
      itemBorderRadius: 6,
      subMenuItemBorderRadius: 6,
      groupTitleFontSize: 11,
      collapsedWidth: SIDER_COLLAPSED_WIDTH,
    },
  },
};

// Collapsed flyouts open to the right of their icon and slide up when they would overflow the
// bottom of the viewport (groups near the end of the bar, e.g. Configuración).
const menuPopupPlacements = {
  rightTop: {
    points: ['tl', 'tr'],
    overflow: { adjustX: 1, shiftY: true },
  },
};

const MainLayoutContent = () => {
  useHomeLocation();
  const { navigate: guardedNavigate } = useNavigationGuard();
  const { t } = useCustomTranslation();
  const { isValidAction } = usePermissions();
  const location = useLocation();
  const activeNavKey = useActiveNavKeyValue();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;

  const [collapsed, setCollapsed] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const can = (page: EPage) => isValidAction(EBasicActions.Find, page);

  // Builds the list of visible leaves: entries whose `visible` flag is false are dropped.
  const leaves = (entries: [boolean, string, string, string[]?][], section?: string): TMenuLeaf[] => entries
    .filter(([visible]) => visible)
    .map(([, key, label, aliases]) => ({
      key, label, section, aliases,
    }));

  const canInventory = can(EPage.ProductInventories);
  const canEntryPlans = can(EPage.EntryPlans);
  const canEstablishments = can(EPage.Establishments);
  const canServiceTypes = can(EPage.ServiceTypes);
  const canTerminalDevices = can(EPage.TerminalDevices);
  const canInvoices = can(EPage.Invoices);
  const canProducts = can(EPage.Products);
  const canProviders = can(EPage.Providers);

  const groups: TMenuGroup[] = [
    {
      key: 'group_operation',
      label: t('sidebar.operation'),
      icon: <ProfileOutlined />,
      children: leaves([
        [can(EPage.Orders), '/orders', t('g.orders')],
        [can(EPage.Deliveries), '/deliveries', t('g.deliverys')],
        [can(EPage.Sales), '/sales', t('sidebar.sales')],
        [can(EPage.Projects), '/projects', t('g.projects')],
        [can(EPage.DeliveryPoints), '/delivery_points', t('g.delivery_points')],
      ]),
    },
    {
      key: 'group_pos',
      label: t('sidebar.pos'),
      icon: <ShopOutlined />,
      children: leaves([
        [canEstablishments, '/establishments/dashboard', t('sidebar.operational_board')],
        [canEstablishments && can(EPage.Accounts), '/accounts', t('g.accounts')],
        [canEstablishments, '/establishments', t('g.establishments')],
        [canEstablishments, '/command_item_processes', t('g.item_processes')],
        [canServiceTypes, '/service_types', t('g.service_types')],
        [canTerminalDevices, '/terminal_devices', t('g.terminal_devices')],
      ]),
    },
    {
      key: 'group_supply',
      label: t('sidebar.supply'),
      icon: <InboxOutlined />,
      children: [
        ...leaves([
          [can(EPage.Solpeds), '/solpeds', t('g.solpeds')],
          [can(EPage.SupplyOrders), '/supply_orders', t('g.supply_orders')],
          [canEntryPlans, '/entry_plans', t('g.entry_plans')],
          [can(EPage.CrossDockings), '/cross_dockings', t('sidebar.cross_docking')],
          [can(EPage.MaterialRequests), '/events/material_requests', t('g.material_requests')],
        ], t('sidebar.section_operations')),
        ...leaves([
          [canProviders, '/providers/external', t('g.providers')],
          [canEntryPlans, '/entry_plans/issues/categories', t('sidebar.entry_issue_categories')],
          [canEntryPlans, '/entry_plans/issues/types', t('sidebar.entry_issue_types')],
        ], t('sidebar.section_catalogs')),
      ],
    },
    {
      key: 'group_inventory_catalog',
      label: t('sidebar.inventory_catalog'),
      icon: <AppstoreOutlined />,
      children: [
        ...leaves([
          [canInventory, '/inventory', t('sidebar.stock')],
          [canInventory && can(EPage.InventoryMovements), '/inventory_movements', t('g.inventory_movements')],
          [canInventory && can(EPage.InventoryConversions), '/inventory/conversions', t('g.inventory_conversions')],
          [canInventory && can(EPage.Folios), '/inventory/folios', t('g.folios')],
        ], t('sidebar.section_inventory')),
        ...leaves([
          [canProducts, '/products', t('sidebar.single_products')],
          [canProducts && can(EPage.Mixes), '/mixes', t('g.submenu_mixes')],
          [can(EPage.Categories), '/categories', t('sidebar.catalog_categories')],
          [can(EPage.Converters), '/converters', t('sidebar.conversion_rules')],
          [can(EPage.Unit), '/units', t('g.units')],
          [can(EPage.Quantifiers), '/quantifiers', t('g.quantifiers')],
        ], t('sidebar.section_catalog')),
      ],
    },
    {
      key: 'group_commercial',
      label: t('sidebar.commercial'),
      icon: <TagsOutlined />,
      children: leaves([
        [can(EPage.Quotes), '/quotes', t('g.quotes')],
        [can(EPage.Quotes) && isValidAction(EBasicActions.Create, EPage.Quotes), '/quotes/new', t('quote.new')],
        [can(EPage.Clients), '/clients', t('sidebar.clients_users')],
        [can(EPage.Discounts), '/discounts', t('g.discounts')],
        [can(EPage.Coupons), '/coupons', t('g.coupons')],
        [can(EPage.MembershipControl), '/membership_control', t('g.memberships')],
        [can(EPage.Badges), '/badges', t('g.badges')],
      ]),
    },
    {
      key: 'group_finance',
      label: t('sidebar.finance'),
      icon: <WalletOutlined />,
      children: leaves([
        [can(EPage.PaymentOrders), '/payments', t('sidebar.payment_orders')],
        [canInvoices, '/invoices', t('g.invoices')],
        [canInvoices && can(EPage.InvoiceParties), '/invoice_parties', t('sidebar.fiscal_entities')],
        [can(EPage.FinancialAccounts), '/financial_accounts', t('g.financial_accounts')],
        [can(EPage.FinancialMovements), '/financial_movements', t('g.financial_movements')],
        [can(EPage.Budgets), '/budgets', t('g.budgets')],
        [can(EPage.CreditLines), '/credit_lines', t('g.credit_lines')],
      ]),
    },
    {
      key: 'group_organization',
      label: t('sidebar.organization'),
      icon: <ApartmentOutlined />,
      children: leaves([
        [can(EPage.MasterFranchises), '/master_franchises/1', t('g.master_franchise'), ['/master_franchises']],
        [can(EPage.Franchises), '/franchises', t('g.franchises')],
        [can(EPage.Operatives), '/operatives', t('g.operatives')],
        [can(EPage.Warehouses), '/warehouses', t('g.warehouses')],
        [canProviders, '/providers/internal', t('g.employees')],
      ]),
    },
    {
      key: 'group_settings',
      label: t('sidebar.settings'),
      icon: <SettingOutlined />,
      children: [
        ...leaves([
          [can(EPage.Roles), '/roles', t('g.roles')],
          [can(EPage.MetaForms), '/meta_forms', t('sidebar.custom_forms')],
          [can(EPage.CustomFields), '/custom_fields', t('g.custom_fields')],
          [can(EPage.PostalCodes), '/postal_codes', t('g.postal_codes')],
          [can(EPage.Banners), '/banners', t('sidebar.screen_notices')],
          [can(EPage.Notifications), '/notifications', t('sidebar.automatic_notifications')],
          [can(EPage.Translations), '/translations', t('g.translations')],
        ]),
        ...leaves([
          [canProviders, '/providers/issues/categories', t('g.issue_categories')],
          [canProviders, '/providers/issues/types', t('g.issue_types')],
        ], t('sidebar.section_providers_employees')),
      ],
    },
  ];

  const visibleGroups = groups.filter((group) => group.children.length > 0);
  const allLeaves = visibleGroups.flatMap((group) => group.children);
  const searchOptions = visibleGroups.flatMap((group) => group.children.map(({ key, label }) => ({
    key, label, groupLabel: group.label,
  })));

  // Consecutive leaves that share a section are wrapped in a text-only header (antd item group).
  const buildChildren = (group: TMenuGroup): TMenuItem[] => group.children.reduce<TMenuItem[]>((items, leaf, index) => {
    const item = { key: leaf.key, label: leaf.label };
    if (!leaf.section) return [...items, item];

    const last = items[items.length - 1];
    if (last && last.type === 'group' && last.label === leaf.section) {
      last.children?.push(item);
      return items;
    }
    return [...items, {
      type: 'group', key: `${group.key}_section_${index}`, label: leaf.section, children: [item],
    }];
  }, []);

  const buildItems = (inlineCollapsed: boolean): MenuProps['items'] => visibleGroups.map((group) => ({
    key: group.key,
    label: group.label,
    icon: group.icon,
    popupClassName: 'main-nav-popup',
    // The collapsed bar only shows icons, so its flyout starts with the group name.
    children: inlineCollapsed
      ? [{ type: 'group', key: `${group.key}_title`, label: group.label, className: 'main-nav-popup-title' }, ...buildChildren(group)]
      : buildChildren(group),
  }));

  const matchesPath = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // The most specific path that matches the current location wins (e.g. /inventory/folios over /inventory).
  const routeKey = allLeaves
    .flatMap((leaf) => [leaf.key, ...(leaf.aliases ?? [])]
      .filter(matchesPath)
      .map((path) => ({ key: leaf.key, length: path.length })))
    .sort((a, b) => b.length - a.length)[0]?.key;

  // Screens whose URL is shared by several options (e.g. /providers/:id) report the right one themselves.
  const selectedKey = activeNavKey && allLeaves.some(({ key }) => key === activeNavKey) ? activeNavKey : routeKey;

  const selectedGroupKey = visibleGroups.find((group) => group.children.some(({ key }) => key === selectedKey))?.key;

  const [inlineOpenKeys, setInlineOpenKeys] = useState<string[]>(selectedGroupKey ? [selectedGroupKey] : []);
  const [popupOpenKeys, setPopupOpenKeys] = useState<string[]>([]);

  // Every navigation (URL, detail screens, Back/Forward) unfolds the active group and closes the flyout.
  useEffect(() => {
    if (selectedGroupKey) {
      setInlineOpenKeys((prev) => (prev.includes(selectedGroupKey) ? prev : [...prev, selectedGroupKey]));
    }
    setPopupOpenKeys([]);
  }, [selectedGroupKey, location.key]);

  // The sider sticks right below the topbar, whose height changes with its content.
  const headerRef = useRef<HTMLElement>(null);
  const [topbarHeight, setTopbarHeight] = useState(56);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;
    const observer = new ResizeObserver(() => setTopbarHeight(header.getBoundingClientRect().height));
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const onClickOption = ({ key }: { key: string }) => {
    setDrawerOpen(false);
    guardedNavigate(key);
  };

  const onClickMenuToggle = () => {
    if (isMobile) {
      setDrawerOpen((prev) => !prev);
    } else {
      setCollapsed((prev) => !prev);
    }
  };

  // "[" toggles the menu, unless the user is typing in a field or holding a modifier.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '[' || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return;
      event.preventDefault();
      if (isMobile) {
        setDrawerOpen((prev) => !prev);
      } else {
        setCollapsed((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile]);

  const menuExpanded = isMobile ? drawerOpen : !collapsed;

  let menuToggleLabel = collapsed ? t('sidebar.expand_menu') : t('sidebar.collapse_menu');
  if (isMobile) {
    menuToggleLabel = drawerOpen ? t('sidebar.close_menu') : t('sidebar.open_menu');
  }

  const renderMenu = (inlineCollapsed: boolean) => (
    <ConfigProvider theme={menuTheme}>
      <Menu
        id="main-nav"
        className="main-nav"
        theme="light"
        mode="inline"
        inlineIndent={13}
        items={buildItems(inlineCollapsed)}
        selectedKeys={selectedKey ? [selectedKey] : []}
        openKeys={inlineCollapsed ? popupOpenKeys : inlineOpenKeys}
        onOpenChange={inlineCollapsed ? setPopupOpenKeys : setInlineOpenKeys}
        triggerSubMenuAction={inlineCollapsed ? 'click' : 'hover'}
        builtinPlacements={menuPopupPlacements}
        onClick={onClickOption}
      />
    </ConfigProvider>
  );

  const footerContent = import.meta.env.VITE_FOOTER;

  return (
    <Layout style={{ minHeight: '100vh', '--topbar-height': `${topbarHeight}px` } as React.CSSProperties}>
      <Header
        ref={headerRef}
        style={{
          padding: 0,
          lineHeight: 'normal',
          height: 'auto',
        }}
        className="topbar"
      >
        <Topbar
          collapsed={!menuExpanded}
          onClickCollapsed={onClickMenuToggle}
          menuToggleLabel={menuToggleLabel}
        />
      </Header>
      <Layout>
        {isMobile ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement="left"
            width="min(304px, calc(100vw - 48px))"
            title={t('sidebar.navigation')}
            rootClassName="main-nav-drawer"
            styles={{ body: { padding: 8 } }}
          >
            <MenuSearch options={searchOptions} compact={false} onSelect={(key) => onClickOption({ key })} />
            {renderMenu(false)}
          </Drawer>
        ) : (
          <Sider
            className="main-nav-sider"
            collapsible
            trigger={null}
            width={SIDER_WIDTH}
            collapsedWidth={SIDER_COLLAPSED_WIDTH}
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
          >
            <MenuSearch options={searchOptions} compact={collapsed} onSelect={(key) => onClickOption({ key })} />
            {renderMenu(collapsed)}
          </Sider>
        )}
        <Content style={{ margin: '32px 16px', minWidth: 0 }}>
          <Outlet />
          {footerContent && (
            <Footer style={{ textAlign: 'center' }}>{footerContent}</Footer>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

const MainLayout = () => (
  <NavigationGuardProvider>
    <ActiveNavProvider>
      <MainLayoutContent />
    </ActiveNavProvider>
  </NavigationGuardProvider>
);

export default MainLayout;
